/**
 * Illustration library.
 *
 * Every scene is authored vector art on a 480x320 stage. Vector art is a
 * deliberate choice, not a placeholder: it stays crisp on an iPad, ships in
 * kilobytes so real offline packages are small (spec §15), and — critically —
 * gives the coloring templates exact fill regions and a genuinely protected
 * outline layer (spec §9.1).
 */

import type { JSX } from 'react';

export const STAGE = { w: 480, h: 320 };

/* ------------------------------------------------------------------ *
 * Shared character parts, so Pip and Sway look like themselves on
 * every page (spec §8.3 — character reference consistency).
 * ------------------------------------------------------------------ */

const FUR = '#E8823C';
const FUR_DARK = '#CE6524';
const CREAM = '#FFF1DC';
const PAW = '#4A2E3E';

function Pip({
  x = 0,
  y = 0,
  s = 1,
  flip = false,
  looking = 'up',
  ears = 'up',
}: {
  x?: number;
  y?: number;
  s?: number;
  flip?: boolean;
  looking?: 'up' | 'side' | 'down';
  /** Flat ears read as frightened — part of the character, not an overlay. */
  ears?: 'up' | 'flat';
}) {
  const eyeY = looking === 'up' ? -1.5 : looking === 'down' ? 1.5 : 0;
  const flat = ears === 'flat';
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      {/* tail */}
      <path
        d="M-30 4 C-52 2 -60 -16 -48 -28 C-42 -34 -32 -32 -30 -22 C-28 -12 -32 -6 -30 4 Z"
        fill={FUR}
      />
      <path d="M-49 -27 C-44 -33 -35 -32 -32 -24 C-38 -26 -45 -24 -49 -27 Z" fill={CREAM} />
      {/* body */}
      <path d="M-32 6 C-32 -14 -18 -26 0 -26 C18 -26 30 -14 30 6 C30 14 22 18 0 18 C-22 18 -32 14 -32 6 Z" fill={FUR} />
      <path d="M-14 8 C-14 -4 -7 -11 0 -11 C7 -11 14 -4 14 8 C14 14 8 16 0 16 C-8 16 -14 14 -14 8 Z" fill={CREAM} />
      {/* legs */}
      <ellipse cx="-14" cy="18" rx="7" ry="4" fill={PAW} />
      <ellipse cx="14" cy="18" rx="7" ry="4" fill={PAW} />
      {/* head */}
      <g transform="translate(14 -30)">
        {flat ? (
          <>
            <path d="M-13 -12 L-32 -6 L-11 0 Z" fill={FUR_DARK} />
            <path d="M13 -12 L32 -6 L11 0 Z" fill={FUR_DARK} />
            <path d="M-14 -11 L-27 -6 L-13 -3 Z" fill="#F3B0A2" />
            <path d="M14 -11 L27 -6 L13 -3 Z" fill="#F3B0A2" />
          </>
        ) : (
          <>
            <path d="M-14 -14 L-22 -30 L-4 -22 Z" fill={FUR_DARK} />
            <path d="M14 -14 L22 -30 L4 -22 Z" fill={FUR_DARK} />
            <path d="M-16 -13 L-20 -25 L-7 -19 Z" fill="#F3B0A2" />
            <path d="M16 -13 L20 -25 L7 -19 Z" fill="#F3B0A2" />
          </>
        )}
        <ellipse cx="0" cy="0" rx="19" ry="17" fill={FUR} />
        <path d="M-8 6 C-8 -2 -3 -6 2 -6 C9 -6 15 0 15 7 C15 12 8 15 2 15 C-4 15 -8 11 -8 6 Z" fill={CREAM} />
        <ellipse cx={-6} cy={-3 + eyeY} rx="3.2" ry="3.6" fill={PAW} />
        <ellipse cx={7} cy={-3 + eyeY} rx="3.2" ry="3.6" fill={PAW} />
        <ellipse cx={-5} cy={-4.4 + eyeY} rx="1.1" ry="1.2" fill="#fff" />
        <ellipse cx={8} cy={-4.4 + eyeY} rx="1.1" ry="1.2" fill="#fff" />
        <ellipse cx="12" cy="7" rx="3.4" ry="2.8" fill={PAW} />
      </g>
    </g>
  );
}

function Sway({ x = 0, y = 0, s = 1, glow = true }: { x?: number; y?: number; s?: number; glow?: boolean }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {glow ? <circle cx="0" cy="0" r="26" fill="#FFD98A" opacity="0.28" /> : null}
      <path d="M-2 2 C-16 -14 -30 -12 -30 0 C-30 12 -16 14 -2 6 Z" fill="#5FC9D6" />
      <path d="M2 2 C16 -14 30 -12 30 0 C30 12 16 14 2 6 Z" fill="#5FC9D6" />
      <path d="M-2 4 C-13 -2 -22 2 -20 8 C-18 14 -8 12 -2 8 Z" fill="#8E6FE0" />
      <path d="M2 4 C13 -2 22 2 20 8 C18 14 8 12 2 8 Z" fill="#8E6FE0" />
      <circle cx="-18" cy="0" r="3" fill="#FFD98A" />
      <circle cx="18" cy="0" r="3" fill="#FFD98A" />
      <ellipse cx="0" cy="4" rx="3.4" ry="9" fill="#3C2A50" />
      <circle cx="0" cy="-5" r="3.6" fill="#3C2A50" />
      <path d="M-2 -8 C-5 -14 -8 -15 -10 -14" stroke="#3C2A50" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <path d="M2 -8 C5 -14 8 -15 10 -14" stroke="#3C2A50" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <circle cx="-1.4" cy="-5.6" r="0.9" fill="#fff" />
      <circle cx="1.6" cy="-5.6" r="0.9" fill="#fff" />
    </g>
  );
}

/* ------------------------------------------------------------------ *
 * Reusable environment pieces
 * ------------------------------------------------------------------ */

function NightSky({ withMoon = true }: { withMoon?: boolean }) {
  const stars = [
    [40, 40, 2.4], [96, 74, 1.6], [150, 32, 2], [210, 60, 1.4], [280, 36, 2.2],
    [330, 72, 1.5], [396, 44, 2], [440, 84, 1.6], [66, 116, 1.4], [364, 118, 1.8],
    [246, 96, 1.3], [420, 150, 1.4], [24, 84, 1.5], [186, 118, 1.2],
  ] as const;
  return (
    <>
      <rect width={STAGE.w} height={STAGE.h} fill="#241B44" />
      <rect width={STAGE.w} height={STAGE.h} fill="url(#skyGrad)" />
      {stars.map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="#FFF4D6" opacity={0.5 + (i % 4) * 0.14} />
      ))}
      {withMoon ? (
        <g>
          <circle cx="392" cy="66" r="34" fill="#FFE9A8" opacity="0.22" />
          <circle cx="392" cy="66" r="24" fill="#FFF0BE" />
          <circle cx="384" cy="60" r="4" fill="#F2DFA4" />
          <circle cx="398" cy="74" r="3" fill="#F2DFA4" />
          <circle cx="400" cy="58" r="2" fill="#F2DFA4" />
        </g>
      ) : null}
    </>
  );
}

function Pond({ moon = true }: { moon?: boolean }) {
  return (
    <>
      <path d="M0 214 C80 200 150 208 220 206 C300 204 400 196 480 206 L480 320 L0 320 Z" fill="#2C4A4E" />
      <ellipse cx="250" cy="268" rx="196" ry="44" fill="#3E7B84" />
      <ellipse cx="250" cy="266" rx="188" ry="39" fill="#4E96A0" />
      {moon ? (
        <g opacity="0.75">
          <ellipse cx="330" cy="262" rx="20" ry="9" fill="#FFF0BE" opacity="0.6" />
          <ellipse cx="330" cy="272" rx="14" ry="5" fill="#FFF0BE" opacity="0.35" />
        </g>
      ) : null}
      <path d="M120 258 q22 -5 44 0" stroke="#7FC4CC" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.7" />
      <path d="M180 282 q26 -6 52 0" stroke="#7FC4CC" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.5" />
      <path d="M300 292 q20 -5 40 0" stroke="#7FC4CC" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.45" />
    </>
  );
}

function Reeds({ x = 0, flip = false }: { x?: number; flip?: boolean }) {
  return (
    <g transform={`translate(${x} 0) ${flip ? 'scale(-1 1)' : ''}`}>
      <path d="M0 236 C-4 210 -2 192 4 176" stroke="#3F7F63" strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M12 238 C10 214 14 198 22 186" stroke="#4C9873" strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M-10 240 C-16 220 -14 206 -8 194" stroke="#356B54" strokeWidth="4" fill="none" strokeLinecap="round" />
      <ellipse cx="4" cy="174" rx="4" ry="10" fill="#8C5A3A" />
      <ellipse cx="22" cy="184" rx="3.6" ry="9" fill="#A06B45" />
    </g>
  );
}

function Willow({ x = 60 }: { x?: number }) {
  return (
    <g transform={`translate(${x} 0)`}>
      <path d="M0 226 C-6 190 -2 160 6 132 L18 132 C22 164 20 196 14 226 Z" fill="#5A3B2E" />
      {Array.from({ length: 9 }).map((_, i) => (
        <path
          key={i}
          d={`M${-34 + i * 10} 120 C${-30 + i * 10} 150 ${-38 + i * 10} 176 ${-32 + i * 10} 198`}
          stroke="#4E8F63"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          opacity={0.55 + (i % 3) * 0.15}
        />
      ))}
      <ellipse cx="6" cy="118" rx="60" ry="30" fill="#3F7B54" />
      <ellipse cx="-14" cy="110" rx="34" ry="20" fill="#4E8F63" />
      <ellipse cx="28" cy="114" rx="30" ry="18" fill="#4E8F63" />
    </g>
  );
}

function RainCurtain({ seed = 1, opacity = 0.5 }: { seed?: number; opacity?: number }) {
  let n = seed * 7919;
  const rnd = () => ((n = (n * 9301 + 49297) % 233280) / 233280);
  const drops = Array.from({ length: 46 }, () => ({
    x: rnd() * 500 - 10,
    y: rnd() * 300,
    len: 16 + rnd() * 22,
  }));
  return (
    <g opacity={opacity} aria-hidden>
      {drops.map((d, i) => (
        <path
          key={i}
          d={`M${d.x} ${d.y} l-7 ${d.len}`}
          stroke="#BFE3EE"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      ))}
    </g>
  );
}

/* ------------------------------------------------------------------ *
 * Daytime garden, for "Pip and the Very Small Visitor".
 *
 * The first two stories are both night pieces. A morning story is here partly
 * because it is a different feeling and partly because it proves the scene
 * library is not a night-time library with some parameters — the same Pip, on
 * the same stage, in a palette that shares nothing with the pond.
 * ------------------------------------------------------------------ */

const LEAF = '#5EA469';
const LEAF_DEEP = '#3C7B4F';
const SHELL = '#D8A05C';
const SHELL_DEEP = '#B5793A';
const SHELL_PALE = '#F3DCB8';
const SNAIL_BODY = '#EFDFCB';
const SNAIL_SHADE = '#D6C2A8';

function MorningGarden({ sun = true }: { sun?: boolean }) {
  return (
    <>
      <rect width={STAGE.w} height={STAGE.h} fill="url(#morningGrad)" />
      {sun ? (
        <g>
          <circle cx="408" cy="54" r="44" fill="#FFE9A8" opacity="0.3" />
          <circle cx="408" cy="54" r="26" fill="#FFF4C8" />
        </g>
      ) : null}
      {/* Three bands of green, lightest furthest away, so the garden has depth
          without a single gradient doing the work. */}
      <path
        d="M0 190 C58 170 112 186 164 176 C224 164 262 184 322 174 C382 164 432 182 480 172 L480 320 L0 320 Z"
        fill="#8CC08E"
      />
      <path
        d="M0 228 C70 214 142 230 212 223 C292 215 362 232 480 221 L480 320 L0 320 Z"
        fill={LEAF}
      />
      <path
        d="M0 266 C92 255 182 270 272 261 C362 252 432 266 480 259 L480 320 L0 320 Z"
        fill={LEAF_DEEP}
      />
    </>
  );
}

function GrassTuft({ x = 0, y = 258, s = 1 }: { x?: number; y?: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M0 0 C-3 -18 -1 -30 4 -40" stroke="#3F8455" strokeWidth="3.5" fill="none" strokeLinecap="round" />
      <path d="M9 2 C8 -14 12 -26 20 -34" stroke="#4E9A63" strokeWidth="3.5" fill="none" strokeLinecap="round" />
      <path d="M-9 2 C-14 -12 -13 -24 -8 -33" stroke="#357248" strokeWidth="3.5" fill="none" strokeLinecap="round" />
    </g>
  );
}

function Flower({ x = 0, y = 250, s = 1, petal = '#F2A8C8' }: { x?: number; y?: number; s?: number; petal?: string }) {
  const petals = [0, 72, 144, 216, 288];
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M0 0 C-2 -14 0 -24 2 -32" stroke="#4E9A63" strokeWidth="3" fill="none" strokeLinecap="round" />
      <g transform="translate(2 -34)">
        {petals.map((deg) => (
          <ellipse key={deg} cx="0" cy="-7" rx="4.6" ry="7" fill={petal} transform={`rotate(${deg})`} />
        ))}
        <circle cx="0" cy="0" r="4" fill="#FFD98A" />
      </g>
    </g>
  );
}

/** A bee: fast, blurry, and gone — the opposite of the visitor. */
function Bee({ x = 0, y = 0, s = 1, flip = false }: { x?: number; y?: number; s?: number; flip?: boolean }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      {/* motion trail, so "quick" is in the drawing rather than only in the words */}
      <path d="M-34 2 q10 -6 20 -2" stroke="#B79A5E" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5" />
      <path d="M-26 8 q9 -5 17 -2" stroke="#B79A5E" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.35" />
      <ellipse cx="-2" cy="-8" rx="9" ry="6" fill="#FFFFFF" opacity="0.65" />
      <ellipse cx="6" cy="-9" rx="7" ry="5" fill="#FFFFFF" opacity="0.5" />
      <ellipse cx="0" cy="0" rx="11" ry="8" fill="#FFD34D" />
      <path d="M-5 -7 q2 14 1 15 M2 -8 q2 15 1 16" stroke="#3A2C18" strokeWidth="3" fill="none" />
      <circle cx="11" cy="-2" r="5" fill="#3A2C18" />
      <circle cx="13" cy="-3.5" r="1.4" fill="#FFF6E2" />
    </g>
  );
}

/**
 * Nib, the snail.
 *
 * Drawn so the shell spiral is the biggest shape on the character — it is what
 * a child will want to colour, and it is what makes the coloring template of
 * the same subject read as the same animal.
 */
function Nib({
  x = 0,
  y = 0,
  s = 1,
  flip = false,
  looking = 'side',
}: {
  x?: number;
  y?: number;
  s?: number;
  flip?: boolean;
  looking?: 'side' | 'up';
}) {
  const eyeY = looking === 'up' ? -4 : 0;
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      {/* foot */}
      <path
        d="M-30 10 C-30 2 -20 -2 -6 -2 C10 -2 26 0 34 4 C40 7 40 12 32 13 L-24 13 C-29 13 -30 12 -30 10 Z"
        fill={SNAIL_BODY}
      />
      <path d="M-26 11 C-14 8 14 8 30 11" stroke={SNAIL_SHADE} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* head + stalks */}
      <g transform="translate(30 -2)">
        <path d="M0 0 C6 -1 10 -5 10 -10" stroke={SNAIL_BODY} strokeWidth="7" fill="none" strokeLinecap="round" />
        <path d="M-2 -2 C2 -8 2 -14 0 -19" stroke={SNAIL_BODY} strokeWidth="4.5" fill="none" strokeLinecap="round" />
        <path d="M-9 -3 C-8 -9 -10 -14 -13 -18" stroke={SNAIL_BODY} strokeWidth="4.5" fill="none" strokeLinecap="round" />
        <circle cx="0" cy={-20 + eyeY} r="3.4" fill="#3A2C3E" />
        <circle cx="-14" cy={-19 + eyeY} r="3.4" fill="#3A2C3E" />
        <circle cx="1" cy={-21 + eyeY} r="1.1" fill="#FFF6E2" />
        <circle cx="-13" cy={-20 + eyeY} r="1.1" fill="#FFF6E2" />
        <path d="M-8 -1 q4 3 8 0" stroke="#B9A188" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      </g>
      {/* shell */}
      <g transform="translate(-4 -14)">
        <circle cx="0" cy="0" r="19" fill={SHELL} />
        <path
          d="M0 -19 A19 19 0 1 1 -13 13 A14 14 0 1 0 3 -5 A8 8 0 1 1 0 10"
          fill="none"
          stroke={SHELL_DEEP}
          strokeWidth="3.4"
          strokeLinecap="round"
        />
        <path d="M-8 -13 A16 16 0 0 1 6 -17" stroke={SHELL_PALE} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.8" />
      </g>
    </g>
  );
}

/** A leaf holding dew, drawn big enough that the drops are the subject. */
function DewLeaf({ x = 0, y = 0, s = 1, drops = 3 }: { x?: number; y?: number; s?: number; drops?: number }) {
  const spots = [
    [-16, -6, 5],
    [6, -12, 6.5],
    [20, 2, 4],
  ] as const;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M-44 6 C-38 -22 -10 -38 22 -34 C48 -31 58 -14 50 6 C42 24 6 30 -18 24 C-34 20 -46 16 -44 6 Z" fill={LEAF} />
      <path d="M-40 8 C-18 0 16 -6 48 2" stroke={LEAF_DEEP} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <path d="M-20 18 C-14 8 -10 2 -4 -6 M4 22 C8 10 12 4 18 -6 M26 20 C28 12 32 6 38 0" stroke={LEAF_DEEP} strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.7" />
      {spots.slice(0, drops).map(([cx, cy, r], i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r={r} fill="#CFEFF6" opacity="0.9" />
          <circle cx={cx - r * 0.3} cy={cy - r * 0.35} r={r * 0.3} fill="#FFFFFF" opacity="0.9" />
        </g>
      ))}
    </g>
  );
}

/** A web with one thread being mended — a thing you only notice if you stop. */
function SpiderWeb({ x = 0, y = 0, s = 1 }: { x?: number; y?: number; s?: number }) {
  const rings = [10, 18, 26];
  const spokes = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {/* Anchor threads first. A web drawn without them floats in mid-air, and
          a five-year-old will notice that before an adult does. */}
      <g stroke="#C6E4EA" strokeWidth="1.1" opacity="0.85">
        <path d="M-26 -26 L-52 -54" />
        <path d="M26 -26 L56 -52" />
        <path d="M-26 26 L-48 62" />
        <path d="M26 26 L50 60" />
      </g>
      <g opacity="0.95">
        {spokes.map((deg) => (
          <path key={deg} d="M0 0 L0 -30" stroke="#D6EDF2" strokeWidth="1.3" transform={`rotate(${deg})`} />
        ))}
        {rings.map((r) => (
          <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="#D6EDF2" strokeWidth="1.3" />
        ))}
      </g>
      {/* The strand she is mending: drawn as a gap, with her working on it. */}
      <path d="M18 -18 L27 -27" stroke="#EAF7FA" strokeWidth="1.3" strokeDasharray="2.5 4" />
      <g transform="translate(19 -19)">
        <path d="M-5 -3 l-5 -4 M-5 1 l-6 2 M5 -3 l5 -4 M5 1 l6 2" stroke="#4A3A52" strokeWidth="1.2" strokeLinecap="round" />
        <ellipse cx="0" cy="0" rx="4.2" ry="3.6" fill="#4A3A52" />
        <circle cx="-1.4" cy="-1.4" r="0.9" fill="#CDBFD6" />
      </g>
    </g>
  );
}

/** A seed shaped like a tiny boat. */
function SeedBoat({ x = 0, y = 0, s = 1 }: { x?: number; y?: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M-14 0 C-10 8 10 8 14 0 C8 3 -8 3 -14 0 Z" fill="#C99A5E" />
      <path d="M-14 0 C-8 -6 8 -6 14 0 Z" fill="#E4BA80" />
      <path d="M0 -2 L0 -16 L9 -10 Z" fill="#FFF3D8" />
      <path d="M0 -16 L0 -2" stroke="#A87F4A" strokeWidth="1.4" strokeLinecap="round" />
    </g>
  );
}

const Defs = (
  <defs>
    <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#1E1640" />
      <stop offset="55%" stopColor="#3A2A63" />
      <stop offset="100%" stopColor="#5B3E72" />
    </linearGradient>
    <linearGradient id="duskGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#5A3E7A" />
      <stop offset="60%" stopColor="#C4708E" />
      <stop offset="100%" stopColor="#F2A97E" />
    </linearGradient>
    <linearGradient id="stormGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#171230" />
      <stop offset="55%" stopColor="#33285A" />
      <stop offset="100%" stopColor="#4C3A6B" />
    </linearGradient>
    <linearGradient id="morningGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#9FD8EC" />
      <stop offset="70%" stopColor="#DCF1F7" />
      <stop offset="100%" stopColor="#FFF6E2" />
    </linearGradient>
    <linearGradient id="paperGlow" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#FFF6D8" />
      <stop offset="100%" stopColor="#FFD98A" />
    </linearGradient>
  </defs>
);

/* ------------------------------------------------------------------ *
 * Scenes
 * ------------------------------------------------------------------ */

export const SCENES: Record<string, () => JSX.Element> = {
  'pip-cover': () => (
    <>
      {Defs}
      <NightSky />
      <Willow x={80} />
      <Pond />
      <Reeds x={392} />
      <Reeds x={60} flip />
      <Pip x={222} y={228} s={1.15} looking="up" />
      <Sway x={318} y={150} s={0.95} />
    </>
  ),

  'pip-p1-goodnight': () => (
    <>
      {Defs}
      <NightSky />
      <Willow x={72} />
      <Pond />
      <Reeds x={400} />
      <Pip x={236} y={230} s={1.05} looking="up" />
      <path
        d="M262 196 q40 -34 96 -68"
        stroke="#FFF0BE"
        strokeWidth="2"
        strokeDasharray="3 7"
        fill="none"
        opacity="0.7"
        strokeLinecap="round"
      />
    </>
  ),

  'pip-p2-empty-sky': () => (
    <>
      {Defs}
      <NightSky withMoon={false} />
      <Willow x={72} />
      <Pond moon={false} />
      <Reeds x={400} />
      <Pip x={236} y={230} s={1.05} looking="up" />
      <g opacity="0.5">
        <circle cx="392" cy="66" r="24" fill="none" stroke="#8E7FB8" strokeWidth="2" strokeDasharray="5 6" />
      </g>
    </>
  ),

  'pip-p3-sway': () => (
    <>
      {Defs}
      <NightSky withMoon={false} />
      <Willow x={64} />
      <Pond moon={false} />
      <Reeds x={404} />
      <Pip x={188} y={230} s={1.05} looking="side" />
      <Sway x={300} y={168} s={1.25} />
      <path d="M330 150 q34 -22 62 -50" stroke="#FFD98A" strokeWidth="2" strokeDasharray="2 6" fill="none" opacity="0.8" strokeLinecap="round" />
    </>
  ),

  'pip-p4-searching': () => (
    <>
      {Defs}
      <NightSky withMoon={false} />
      <Willow x={392} />
      <Pond moon={false} />
      <Reeds x={110} />
      <Reeds x={168} flip />
      <Reeds x={62} />
      <Pip x={148} y={236} s={1} looking="down" flip />
      <Sway x={250} y={196} s={0.85} />
      <g opacity="0.85">
        <circle cx="330" cy="212" r="22" fill="none" stroke="#FFF0BE" strokeWidth="2.5" />
        <path d="M346 228 l16 16" stroke="#FFF0BE" strokeWidth="4" strokeLinecap="round" />
      </g>
    </>
  ),

  'pip-p5-idea': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#2B2050" />
      <rect width={STAGE.w} height={STAGE.h} fill="url(#skyGrad)" opacity="0.7" />
      {/* burrow interior */}
      <path d="M0 250 L480 250 L480 320 L0 320 Z" fill="#4A3324" />
      <path d="M0 244 C120 232 360 232 480 244 L480 254 L0 254 Z" fill="#5C4130" />
      <ellipse cx="240" cy="150" rx="200" ry="120" fill="#3B2A1E" opacity="0.55" />
      {/* lantern */}
      <g transform="translate(96 140)">
        <path d="M0 -30 L0 -10" stroke="#8C6A4A" strokeWidth="3" strokeLinecap="round" />
        <path d="M-16 -10 L16 -10 L12 26 L-12 26 Z" fill="url(#paperGlow)" />
        <circle cx="0" cy="6" r="26" fill="#FFD98A" opacity="0.22" />
      </g>
      {/* paper sheets */}
      <g>
        <rect x="286" y="216" width="66" height="46" rx="4" fill="#FFF4DC" transform="rotate(-8 319 239)" />
        <rect x="316" y="204" width="66" height="46" rx="4" fill="#FFEFCC" transform="rotate(7 349 227)" />
        <path d="M300 212 l14 -16 l6 6 Z" fill="#E8D9B6" />
      </g>
      <Pip x={200} y={236} s={1.1} looking="side" />
      <Sway x={294} y={168} s={0.8} />
      {/* idea sparkle */}
      <g transform="translate(232 152)">
        <path d="M0 -16 C1.6 -5 5 -1.6 16 0 C5 1.6 1.6 5 0 16 C-1.6 5 -5 1.6 -16 0 C-5 -1.6 -1.6 -5 0 -16 Z" fill="#FFE07A" />
      </g>
    </>
  ),

  'pip-p6-paper-moon': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#241B44" />
      <rect width={STAGE.w} height={STAGE.h} fill="url(#skyGrad)" />
      {[[40, 40, 2.4], [150, 32, 2], [280, 36, 2.2], [440, 84, 1.6], [66, 116, 1.4]].map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="#FFF4D6" opacity="0.7" />
      ))}
      <g>
        <circle cx="330" cy="88" r="52" fill="#FFD98A" opacity="0.22" />
        <circle cx="330" cy="88" r="36" fill="#FFD98A" opacity="0.3" />
        <path
          d="M330 52 L340 76 L366 78 L346 94 L353 120 L330 106 L307 120 L314 94 L294 78 L320 76 Z"
          fill="url(#paperGlow)"
        />
        <path d="M330 52 L330 106 L307 120 L314 94 L294 78 L320 76 Z" fill="#FFF6D8" opacity="0.6" />
        <path d="M330 124 L330 148" stroke="#D9C9A0" strokeWidth="1.6" strokeDasharray="3 4" />
      </g>
      <Willow x={72} />
      <Pond />
      <Reeds x={430} />
      <Pip x={214} y={232} s={1.1} looking="up" />
      <Sway x={288} y={160} s={0.9} />
      <ellipse cx="300" cy="272" rx="26" ry="10" fill="#FFF0BE" opacity="0.45" />
    </>
  ),

  /* Activity option thumbnails */
  'opt-moon': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#2E2356" rx="0" />
      <circle cx="240" cy="160" r="72" fill="#FFF0BE" />
      <circle cx="214" cy="136" r="12" fill="#F2DFA4" />
      <circle cx="262" cy="186" r="9" fill="#F2DFA4" />
    </>
  ),
  'opt-sun': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#8FD3E8" />
      <circle cx="240" cy="160" r="60" fill="#FFC94D" />
      {Array.from({ length: 10 }).map((_, i) => (
        <rect key={i} x="236" y="60" width="8" height="26" rx="4" fill="#FFC94D" transform={`rotate(${i * 36} 240 160)`} />
      ))}
    </>
  ),
  'opt-cloud': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#9CC7E8" />
      <ellipse cx="210" cy="176" rx="66" ry="44" fill="#FFFFFF" />
      <ellipse cx="272" cy="184" rx="52" ry="34" fill="#F2F7FC" />
      <ellipse cx="248" cy="146" rx="44" ry="32" fill="#FFFFFF" />
    </>
  ),
  'opt-pip': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#F6E4CF" />
      <Pip x={240} y={230} s={1.9} looking="side" />
    </>
  ),
  'opt-sway': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#EADFF6" />
      <Sway x={240} y={160} s={3} glow={false} />
    </>
  ),
  'opt-paper': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#F4EAD6" />
      <rect x="150" y="94" width="120" height="140" rx="8" fill="#FFF8E6" transform="rotate(-7 210 164)" />
      <rect x="204" y="104" width="120" height="140" rx="8" fill="#FFF2D4" transform="rotate(6 264 174)" />
    </>
  ),

  /* ------------------------------------------------------------- *
   * Story 2 — Pip and the Loudest Night
   * Same characters, different weather. Reuses Pip, Sway, Pond, Willow
   * and Reeds unchanged, which is what makes the recurring-character
   * promise real rather than decorative (spec §8.3, F18).
   * ------------------------------------------------------------- */

  'storm-cover': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#2A2440" />
      <rect width={STAGE.w} height={STAGE.h} fill="url(#stormGrad)" />
      <RainCurtain seed={2} />
      <Willow x={96} />
      <Pond moon={false} />
      <Reeds x={410} />
      <Pip x={232} y={232} s={1.1} looking="up" />
      <Sway x={310} y={158} s={0.9} />
      <path d="M120 40 L150 96 L126 100 L156 150" stroke="#FFE07A" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </>
  ),

  'storm-p1-wind': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#2A2440" />
      <rect width={STAGE.w} height={STAGE.h} fill="url(#stormGrad)" />
      <g opacity="0.55">
        {[70, 118, 166].map((y, i) => (
          <path key={y} d={`M${20 + i * 30} ${y} q60 -14 120 0 t120 0`} stroke="#CBB9E8" strokeWidth="3" fill="none" strokeLinecap="round" />
        ))}
      </g>
      <g transform="rotate(-7 96 200)"><Willow x={96} /></g>
      <Pond moon={false} />
      <Reeds x={418} />
      <Pip x={240} y={234} s={1.05} looking="side" />
    </>
  ),

  'storm-p2-thunder': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#1A1530" />
      <rect width={STAGE.w} height={STAGE.h} fill="#FFF6D8" opacity="0.16" />
      <RainCurtain seed={5} />
      <path d="M300 20 L338 92 L308 96 L344 168" stroke="#FFE07A" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <Willow x={92} />
      <Pond moon={false} />
      <Pip x={236} y={236} s={1.05} looking="down" ears="flat" />
    </>
  ),

  'storm-p3-sway-calls': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#251E3C" />
      <RainCurtain seed={9} />
      <Willow x={110} />
      <Pond moon={false} />
      <Reeds x={430} />
      {/* a gap between the willow roots */}
      <path d="M74 232 C74 196 126 196 126 232 Z" fill="#150F26" />
      <Sway x={100} y={214} s={0.85} />
      <Pip x={264} y={236} s={1.05} looking="side" flip />
      <path d="M132 214 q40 -10 92 0" stroke="#FFD98A" strokeWidth="2" strokeDasharray="2 7" fill="none" opacity="0.85" strokeLinecap="round" />
    </>
  ),

  'storm-p4-den': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#1E1834" />
      <RainCurtain seed={13} opacity={0.35} />
      {/* leaf den */}
      <g>
        <path d="M60 300 C60 200 200 150 250 150 C300 150 420 200 420 300 Z" fill="#3F7B54" />
        <path d="M92 300 C92 216 206 176 250 176 C296 176 390 216 390 300 Z" fill="#2C5C3E" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <ellipse key={i} cx={110 + i * 56} cy={190 + (i % 2) * 22} rx="40" ry="20" fill="#4E8F63" opacity={0.75} transform={`rotate(${-18 + i * 8} ${110 + i * 56} ${190 + (i % 2) * 22})`} />
        ))}
      </g>
      <ellipse cx="250" cy="300" rx="120" ry="26" fill="#5C4130" />
      <Pip x={228} y={296} s={1} looking="side" ears="flat" />
      <Sway x={302} y={252} s={0.7} />
    </>
  ),

  'storm-p5-morning': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#BFE3EE" />
      <rect width={STAGE.w} height={STAGE.h} fill="url(#morningGrad)" />
      <circle cx="392" cy="70" r="30" fill="#FFD98A" />
      {[0, 1, 2].map((i) => (
        <ellipse key={i} cx={90 + i * 130} cy={64 + (i % 2) * 26} rx="46" ry="18" fill="#FFFFFF" opacity="0.8" />
      ))}
      <Willow x={78} />
      <path d="M0 216 C90 206 200 214 300 208 C380 204 430 210 480 206 L480 320 L0 320 Z" fill="#6FA85F" />
      {/* puddles full of sky */}
      <ellipse cx="230" cy="272" rx="128" ry="34" fill="#8FD3E8" />
      <ellipse cx="230" cy="268" rx="118" ry="28" fill="#BFE9F6" />
      <ellipse cx="392" cy="252" rx="20" ry="8" fill="#FFD98A" opacity="0.7" />
      <Pip x={318} y={224} s={0.95} looking="down" flip />
      <Sway x={140} y={168} s={0.75} />
    </>
  ),

  /* Story 2 activity thumbnails */
  'opt-den': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#E7F0E2" />
      <path d="M96 260 C96 176 200 132 240 132 C280 132 384 176 384 260 Z" fill="#4E8F63" />
      <path d="M132 260 C132 194 210 158 240 158 C270 158 348 194 348 260 Z" fill="#2C5C3E" />
    </>
  ),
  'opt-nest': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#F4E7D2" />
      <ellipse cx="240" cy="210" rx="110" ry="60" fill="#A06B45" />
      <ellipse cx="240" cy="196" rx="82" ry="40" fill="#7A4F31" />
      <circle cx="216" cy="196" r="20" fill="#FFF4DC" />
      <circle cx="262" cy="200" r="20" fill="#FFF4DC" />
    </>
  ),
  'opt-boat': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#CDE9F2" />
      <path d="M120 200 L360 200 L320 260 L160 260 Z" fill="#E8823C" />
      <path d="M240 200 L240 108 L322 186 Z" fill="#FFF4DC" />
    </>
  ),
  'opt-rain': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#5B4E7A" />
      {Array.from({ length: 16 }).map((_, i) => (
        <path key={i} d={`M${30 + i * 28} ${60 + (i % 4) * 44} l-8 34`} stroke="#BFE3EE" strokeWidth="5" strokeLinecap="round" />
      ))}
    </>
  ),
  'opt-scared': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#2A2440" />
      <path d="M300 30 L338 102 L308 106 L344 178" stroke="#FFE07A" strokeWidth="9" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <Pip x={190} y={250} s={1.6} looking="down" ears="flat" />
    </>
  ),

  /* Marketing / world scenes */
  /**
   * My World. The empty containers here are drawn to match WORLD_SLOTS
   * exactly (see src/services/editions.ts) so a child's picture lands inside
   * a frame rather than floating over the furniture.
   * Stage is 480x320; slot boxes are percentages of that.
   */
  'world-room': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#F6E7D5" />
      <rect y="252" width={STAGE.w} height="68" fill="#C9A683" />
      <rect y="248" width={STAGE.w} height="6" fill="#A9855F" />

      {/* Wall frames — slots wall-frame:0 and wall-frame:1 */}
      {[32, 134].map((y) => (
        <g key={y}>
          <rect x="18" y={y - 6} width="137" height="95" rx="6" fill="#B0846014" />
          <rect x="20" y={y - 4} width="133" height="91" rx="5" fill="#8C5A3A" />
          <rect x="24" y={y} width="125" height="83" rx="3" fill="#FFFBF2" />
        </g>
      ))}

      {/* Window — slot window:0 */}
      <g>
        <rect x="294" y="30" width="160" height="125" rx="8" fill="#8C5A3A" />
        <rect x="302" y="38" width="144" height="109" rx="4" fill="#BFE3EE" />
        <circle cx="420" cy="62" r="15" fill="#FFF0BE" />
        <circle cx="330" cy="120" r="26" fill="#FFFFFF" opacity="0.5" />
        <path d="M374 38 v109 M302 92 h144" stroke="#8C5A3A" strokeWidth="5" />
      </g>

      {/* Shelf — slot shelf:0 sits on the board */}
      <g>
        <rect x="165" y="237" width="122" height="9" rx="3" fill="#A9855F" />
        <rect x="171" y="246" width="10" height="7" fill="#8C5A3A" />
        <rect x="271" y="246" width="10" height="7" fill="#8C5A3A" />
      </g>

      {/* Plant pot — slot garden:0 grows out of it */}
      <g>
        <path d="M182 288 L260 288 L252 316 L190 316 Z" fill="#D2695E" />
        <rect x="177" y="280" width="88" height="10" rx="3" fill="#E07C6E" />
      </g>

      {/* The fox keeps out of every placement box. */}
      <g transform="translate(392 300) scale(0.72)">
        <Pip x={0} y={0} s={1} looking="side" />
      </g>
      <ellipse cx="392" cy="306" rx="46" ry="8" fill="#9E7C5C" opacity="0.3" />
    </>
  ),

  'story-forest': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#D9EDD6" />
      <circle cx="392" cy="70" r="34" fill="#FFD98A" />
      {[40, 120, 200, 280, 360, 440].map((x, i) => (
        <g key={x}>
          <rect x={x - 6} y={180 - i * 4} width="12" height="90" fill="#7A5637" />
          <path d={`M${x} ${100 - i * 6} L${x + 46} ${192 - i * 4} L${x - 46} ${192 - i * 4} Z`} fill={i % 2 ? '#4E8F63' : '#3F7B54'} />
        </g>
      ))}
      <rect y="252" width={STAGE.w} height="68" fill="#6FA85F" />
    </>
  ),

  'story-space': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#1B1638" />
      {Array.from({ length: 22 }).map((_, i) => (
        <circle key={i} cx={(i * 71) % 470 + 6} cy={(i * 133) % 300 + 10} r={i % 3 ? 1.6 : 2.6} fill="#FFF4D6" opacity="0.8" />
      ))}
      <circle cx="150" cy="180" r="56" fill="#E2669B" />
      <ellipse cx="150" cy="180" rx="90" ry="18" fill="none" stroke="#FFD98A" strokeWidth="7" transform="rotate(-18 150 180)" />
      <circle cx="360" cy="104" r="26" fill="#5FC9D6" />
      <circle cx="410" cy="216" r="16" fill="#8E6FE0" />
    </>
  ),

  'story-home': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#FDE7CE" />
      <circle cx="80" cy="66" r="34" fill="#FFD98A" />
      <rect y="230" width={STAGE.w} height="90" fill="#8FBF7A" />
      <path d="M160 230 L160 140 L240 88 L320 140 L320 230 Z" fill="#F4C99B" />
      <path d="M144 146 L240 78 L336 146 L322 158 L240 100 L158 158 Z" fill="#D2695E" />
      <rect x="214" y="176" width="52" height="54" rx="6" fill="#8C5A3A" />
      <rect x="176" y="158" width="32" height="30" rx="5" fill="#BFE3EE" />
      <rect x="272" y="158" width="32" height="30" rx="5" fill="#BFE3EE" />
    </>
  ),
  /* ---------------- Pip and the Very Small Visitor ---------------- */

  'nib-cover': () => (
    <>
      {Defs}
      <MorningGarden />
      <GrassTuft x={60} y={262} s={1.1} />
      <Flower x={110} y={256} s={1.1} />
      <Flower x={402} y={250} s={1} petal="#FFD98A" />
      <GrassTuft x={430} y={266} s={1.2} />
      <DewLeaf x={300} y={252} s={1.25} />
      <Nib x={296} y={238} s={1.2} />
      <Pip x={170} y={244} s={1.05} looking="side" flip />
      <Sway x={92} y={120} s={0.7} glow={false} />
    </>
  ),

  'nib-p1-busy': () => (
    <>
      {Defs}
      <MorningGarden />
      <GrassTuft x={44} y={256} s={1} />
      <Flower x={96} y={252} s={1} />
      <Flower x={148} y={246} s={0.9} petal="#B6E0F2" />
      <Flower x={392} y={252} s={1.05} petal="#FFD98A" />
      <GrassTuft x={446} y={264} s={1.15} />
      {/* Everything in this picture is mid-movement. */}
      <Bee x={214} y={128} s={1.05} />
      <Bee x={330} y={92} s={0.8} flip />
      <Bee x={118} y={162} s={0.7} />
      <path d="M40 108 q40 -16 84 -4" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.55" />
      <path d="M300 150 q46 -18 96 -6" stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.45" />
      <Pip x={244} y={244} s={1.1} looking="up" />
    </>
  ),

  'nib-p2-found': () => (
    <>
      {Defs}
      <MorningGarden />
      <GrassTuft x={56} y={262} s={1.1} />
      <Flower x={420} y={254} s={1} petal="#F2A8C8" />
      <GrassTuft x={452} y={268} s={1.1} />
      <DewLeaf x={318} y={236} s={1.5} />
      <Nib x={312} y={218} s={1.4} looking="side" />
      <Pip x={156} y={248} s={1} looking="side" flip />
    </>
  ),

  'nib-p3-where': () => (
    <>
      {Defs}
      <MorningGarden sun={false} />
      <GrassTuft x={40} y={258} s={1} />
      <GrassTuft x={462} y={264} s={1.1} />
      <Flower x={78} y={252} s={0.95} petal="#B6E0F2" />
      {/* Pip is down at the snail's height. The whole page is that decision. */}
      <DewLeaf x={286} y={250} s={1.6} />
      <Nib x={280} y={230} s={1.55} looking="up" />
      <Pip x={150} y={256} s={1.15} looking="side" flip />
      <Sway x={396} y={132} s={0.62} glow={false} />
    </>
  ),

  'nib-p4-waiting': () => (
    <>
      {Defs}
      <MorningGarden />
      <GrassTuft x={30} y={254} s={0.9} />
      <GrassTuft x={96} y={262} s={1.2} />
      <GrassTuft x={396} y={258} s={1} />
      <GrassTuft x={456} y={266} s={1.3} />
      <Flower x={62} y={250} s={0.9} petal="#FFD98A" />
      <Flower x={424} y={252} s={1} />
      <SpiderWeb x={96} y={132} s={1.1} />
      <SeedBoat x={370} y={276} s={1.05} />
      <DewLeaf x={268} y={244} s={1.45} />
      <Nib x={262} y={226} s={1.35} />
      <Pip x={178} y={252} s={1} looking="side" flip />
    </>
  ),

  'nib-p5-close': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="url(#morningGrad)" />
      {/* A close-up: the garden got bigger because he got slower. */}
      <path d="M0 214 C90 196 200 216 300 202 C380 190 440 208 480 198 L480 320 L0 320 Z" fill={LEAF} />
      <path d="M0 268 C110 256 210 272 320 262 C400 254 448 266 480 260 L480 320 L0 320 Z" fill={LEAF_DEEP} />
      <SpiderWeb x={356} y={110} s={2} />
      <SeedBoat x={128} y={236} s={2.4} />
      <DewLeaf x={252} y={266} s={1.9} drops={3} />
      <Nib x={362} y={258} s={1} flip />
    </>
  ),

  'nib-p6-here': () => (
    <>
      {Defs}
      <MorningGarden />
      <GrassTuft x={50} y={260} s={1.1} />
      <Flower x={104} y={252} s={1} />
      <Flower x={406} y={250} s={1.05} petal="#B6E0F2" />
      <GrassTuft x={448} y={266} s={1.2} />
      <SpiderWeb x={62} y={108} s={0.85} />
      <DewLeaf x={296} y={248} s={1.4} />
      <Nib x={290} y={230} s={1.3} looking="up" />
      <Pip x={182} y={250} s={1.05} looking="side" flip />
      <Sway x={358} y={124} s={0.72} />
    </>
  ),

  /* Activity option art for this story. */

  'opt-snail': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#E8F6E9" />
      <Nib x={200} y={200} s={2.6} looking="up" />
    </>
  ),

  'opt-bee': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#FFF6E2" />
      <Bee x={240} y={160} s={3.2} />
    </>
  ),

  'opt-wind': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#E6F3FA" />
      <g stroke="#8FB9CC" strokeWidth="10" fill="none" strokeLinecap="round">
        <path d="M80 120 q100 -34 190 -6" />
        <path d="M110 170 q110 -34 220 -4" />
        <path d="M70 220 q96 -30 176 -6" />
      </g>
      <path d="M300 160 q40 -16 40 12 t-34 10" stroke="#8FB9CC" strokeWidth="10" fill="none" strokeLinecap="round" />
    </>
  ),

  'opt-nib-found': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#E8F6E9" />
      <DewLeaf x={240} y={196} s={1.9} />
      <Nib x={234} y={174} s={1.7} />
    </>
  ),

  'opt-nib-wait': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#E8F6E9" />
      <Pip x={200} y={216} s={1.6} looking="side" flip />
      <Nib x={330} y={228} s={1.1} />
    </>
  ),

  'opt-nib-see': () => (
    <>
      {Defs}
      <rect width={STAGE.w} height={STAGE.h} fill="#E8F6E9" />
      <SpiderWeb x={150} y={130} s={2.1} />
      <SeedBoat x={320} y={212} s={2.6} />
    </>
  ),
};

export function Scene({
  id,
  className,
  title,
  rounded = true,
  fit = 'cover',
}: {
  id: string;
  className?: string;
  title?: string;
  rounded?: boolean;
  /**
   * 'cover' fills the box and may crop. 'contain' shows the whole stage —
   * required wherever an overlay is positioned against the scene, such as My
   * World, so slot boxes stay aligned with the furniture.
   */
  fit?: 'cover' | 'contain';
}) {
  const Draw = SCENES[id];
  if (!Draw) {
    return (
      <div
        className={className}
        role="img"
        aria-label={title ?? 'Illustration unavailable'}
        style={{ background: 'rgb(var(--c-paper-deep))' }}
      />
    );
  }
  return (
    <svg
      viewBox={`0 0 ${STAGE.w} ${STAGE.h}`}
      className={className}
      role="img"
      aria-label={title ?? ''}
      aria-hidden={title ? undefined : true}
      preserveAspectRatio={fit === "contain" ? "xMidYMid meet" : "xMidYMid slice"}
      style={rounded ? { display: 'block' } : undefined}
    >
      <Draw />
    </svg>
  );
}
